<template>
  <div class="container">
    <!-- Title with Back Arrow -->
    <!-- <h1 class="reviews-title">
      <v-icon @click="goBack" class="back-arrow">mdi-arrow-left</v-icon>
      
      Latest Review
    </h1> -->

    <!-- Loader -->
    <div v-if="isLoading" class="loader">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Loading...</p>
    </div>

    <!-- Toggle Buttons -->
    <div class="filter-buttons">
      <div class="d-flex align-center w-100">
        <v-select
          v-model="selectedFilter"
          :items="filterOptions"
          label="Filter Locations"
          outlined
          dense
          @change="applyFilter"
          class="mr-2 filter-select"
          hide-details
        ></v-select>
        <v-btn
          color="primary"
          icon
          @click="captureScreenshot"
          title="Save page as image"
        >
          <v-icon>mdi-download-box-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="search-bar">
      <input
        type="text"
        v-model="searchTerm"
        placeholder="Search locations..."
        @input="filterLocations"
      />
    </div>

    <div
      v-if="filteredLocations.length === 0 && !isLoading"
      class="text-center py-8"
    >
      <div class="p-8 bg-white rounded-lg shadow-sm max-w-3xl mx-auto">
        <p class="text-gray-500 mb-4">
          No locations found. Please check your filters or connect Google
          Business locations.
        </p>
      </div>
    </div>
    <div v-else>
      <table class="locations-table">
        <thead>
          <tr>
            <th>Location</th>
            <th>Area</th>
            <th>City</th>
            <th>State, Zip, Country</th>
            <th>Last Received Review</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="location in filteredAndSearchedLocations.slice(
              (currentPage - 1) * itemsPerPage,
              currentPage * itemsPerPage
            )"
            :key="location.id"
          >
            <td>{{ location.storeLocation }}</td>
            <td>{{ location.area }}</td>
            <td>{{ location.city }}</td>
            <td>{{ location.stateZipCountry }}</td>
            <td>
              <span v-if="location.lastReviewUpdateTime === 'N/A'">
                N/A
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      small
                      v-bind="attrs"
                      v-on="on"
                      class="info-icon"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>No reviews received since connection with Fly</span>
                </v-tooltip>
              </span>
              <span v-else>{{ location.lastReviewUpdateTime }}</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination Controls -->
      <div class="pagination">
        <div class="pagination-controls">
          <button
            class="pagination-button"
            @click="prevPage"
            :disabled="currentPage === 1"
          >
            Previous
          </button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="pagination-button"
            @click="nextPage"
            :disabled="currentPage === totalPages"
          >
            Next
          </button>
        </div>
        <div class="items-per-page">
          <v-select
            v-model="itemsPerPage"
            :items="itemsPerPageOptions"
            label="Items per page"
            outlined
            dense
            hide-details
            class="items-per-page-select"
            @change="handleItemsPerPageChange"
          ></v-select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "@/utils/firebase.utils"; // Adjust the import based on your project structure
import {
  collection,
  onSnapshot,
  where,
  query,
  getDocs,
} from "firebase/firestore";
import EmptyState from "@/components/ui/common/EmptyState.vue";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import html2canvas from "html2canvas"; // Add this import

export default {
  components: {
    EmptyState,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      locations: [], // Array to hold fetched locations
      filteredLocations: [], // Array to hold filtered locations
      noReview: [], // Array to hold locations without reviews
      noReviewLastWeek: [], // Array to hold locations without reviews for past week
      selectedFilter: "noReview", // Default filter
      filterOptions: [
        { text: "Locations without Reviews", value: "noReview" },
        { text: "Unreviewed Last Week", value: "unreviewedLastWeek" },
        { text: "All Locations", value: "all" },
      ],
      isLoading: false, // Loading state
      currentPage: 1, // Current page for pagination
      itemsPerPage: 10, // Default number of items per page
      itemsPerPageOptions: [
        { text: "10", value: 10 },
        { text: "20", value: 20 },
        { text: "50", value: 50 },
        { text: "100", value: 100 },
      ],
      searchTerm: "", // New data property for search term
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredLocations.length / this.itemsPerPage);
    },
    paginatedLocations() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredLocations.slice(start, end);
    },
    filteredAndSearchedLocations() {
      return this.filteredLocations.filter((location) =>
        location.storeLocation
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase())
      );
    },
  },
  methods: {
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    applyFilter() {
      this.searchTerm = ""; // Reset search term
      this.currentPage = 1; // Reset to first page
      if (this.selectedFilter === "noReview") {
        this.filteredLocations = this.noReview;
      } else if (this.selectedFilter === "unreviewedLastWeek") {
        this.filteredLocations = this.noReviewLastWeek;
      } else {
        this.filteredLocations = this.locations; // 'all'
      }
    },
    async fetchLocations() {
      const uid = this.currentUID; // Get the current user ID
      const locationsRef = collection(db, "users", uid, "fly_workspaces");
      const locationQuery = query(
        locationsRef,
        where("admin", "==", this.WID),
        where("storeWorkspace", "==", true)
      );
      const querySnapshot = await getDocs(locationQuery);
      // Listen for real-time updates
      this.locations = []; // Clear existing locations
      this.noReview = []; // Clear existing noReview array
      this.noReviewLastWeek = []; // Clear existing noReviewLastWeek array

      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Calculate one week ago

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        // console.log('Fetched document data:', JSON.stringify(docData, null, 2)); // Log the document data, removed as it's no longer needed
        let lastReviewUpdateTime = "N/A"; // Default value
        let noReviewLastWeek = false;
        // Check conditions
        if (
          (docData.storeWorkspace &&
            docData.admin === this.WID &&
            docData.connected_apps?.includes("google_business_profile")) ||
          docData.shallowConnect
        ) {
          // Check lastReviewUpdateTime
          if (docData.lastReviewUpdateTime) {
            const reviewTime = docData.lastReviewUpdateTime.toDate(); // Convert Firestore timestamp to Date
            lastReviewUpdateTime = reviewTime.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }); // Format date
            if (reviewTime <= oneWeekAgo) {
              noReviewLastWeek = true;
            }
          }

          // Add document ID and data to the locations array
          const location = {
            id: doc.id,
            storeLocation: docData.storeLocation,
            area:
              docData.gmbProfile?.storefrontAddress?.addressLines?.[0]
                ?.split(",")[0]
                ?.trim() || "N/A",
            city: docData.gmbProfile?.storefrontAddress?.locality || "N/A",
            stateZipCountry: `${
              docData.gmbProfile?.storefrontAddress?.administrativeArea || "N/A"
            }, ${docData.gmbProfile?.storefrontAddress?.postalCode || "N/A"}, ${
              docData.gmbProfile?.storefrontAddress?.regionCode || "N/A"
            }`,
            lastReviewUpdateTime,
          };
          this.locations.push(location);

          // Populate noReview array
          if (lastReviewUpdateTime === "N/A") {
            this.noReview.push(location);
          }

          if (noReviewLastWeek || lastReviewUpdateTime === "N/A") {
            this.noReviewLastWeek.push(location);
          }
        }
      });
      this.filteredLocations = this.locations;
      this.applyFilter(); // Apply initial filter based on selectedFilter
      this.currentPage = 1; // Reset to first page when data changes
    },
    goBack() {
      this.$router.push("/gmb-reviews"); // Navigate back to the specified route
    },
    filterLocations() {
      this.currentPage = 1; // Reset to first page when filtering
    },
    async captureScreenshot() {
      try {
        // Show a loading indicator
        this.$store.commit("showSnackbar", {
          text: "Preparing screenshot...",
          color: "info",
        });

        // Get the container element to capture
        const containerElement = document.querySelector(".container");
        if (!containerElement) {
          throw new Error("Container element not found");
        }

        // Use html2canvas to capture the element
        const canvas = await html2canvas(containerElement, {
          scale: 2, // Higher quality
          useCORS: true, // Allow cross-origin images
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Convert to image and download
        const imageData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageData;

        // Generate filename with current date
        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `no-review-locations-${formattedDate}.png`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Success message
        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.$snackbar.show("Failed to save screenshot");
      }
    },
    handleItemsPerPageChange() {
      this.currentPage = 1; // Reset to first page when changing items per page
      // Scroll to the top of the table
      const tableElement = document.querySelector(".locations-table");
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
  mounted() {
    this.fetchLocations(); // Fetch locations on mount
  },
};
</script>

<style scoped>
.container {
  margin: 0 20px; /* Add left and right margin */
}

.reviews-title {
  font-size: 24px; /* Adjust font size */
  font-weight: bold; /* Make the title bold */
  color: #1a1a1a; /* Change color to match the design */
  margin-bottom: 20px; /* Add space below the title */
  display: flex; /* Use flexbox for alignment */
  align-items: center; /* Center items vertically */
}

.back-arrow {
  cursor: pointer; /* Change cursor to pointer */
  margin-right: 10px; /* Add space between arrow and title */
  background-color: #f0f0f0; /* Light background color */
  border-radius: 50%; /* Make it circular */
  padding: 5px; /* Add some padding */
  font-size: 18px; /* Adjust font size */
  line-height: 1; /* Center the arrow vertically */
}

.filter-buttons {
  display: flex;
  margin-bottom: 20px;
  justify-content: space-between;
  width: 100%;
}

.filter-select {
  min-width: 250px !important;
  max-width: 400px !important;
}

.filter-select ::v-deep .v-select__selections {
  max-width: 100%;
  overflow: visible;
  white-space: nowrap;
}

.filter-select ::v-deep .v-select__selection {
  max-width: none;
  overflow: visible;
  text-overflow: unset;
}

.filter-buttons .v-select {
  flex-grow: 1;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0; /* Add some margin for spacing */
}

.locations-table {
  width: calc(100% - 20px); /* Adjust width to account for right margin */
  border-collapse: collapse;
  margin-top: 20px;
  margin-right: 20px; /* Add right margin */
}

.locations-table th,
.locations-table td {
  border: 1px solid #e5e7eb;
  padding: 10px;
  text-align: left;
}

.locations-table th {
  background-color: #007bff; /* Blue shade for the header */
  color: white; /* White text color for contrast */
}

.active {
  background-color: #007bff; /* Highlight color for active filter */
  color: white; /* Change text color for contrast */
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 0 20px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.items-per-page {
  min-width: 150px;
}

.items-per-page-select {
  width: 150px;
}

.pagination-button {
  cursor: pointer;
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: white;
  color: #007bff;
  font-weight: bold;
  transition: all 0.2s ease;
}

.pagination-button:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.pagination-button:disabled {
  color: #ccc;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

/* Adjust the v-select styles */
.items-per-page ::v-deep .v-select__selections {
  min-height: 32px;
}

.items-per-page ::v-deep .v-input__slot {
  min-height: 32px;
}

.search-bar {
  margin-bottom: 20px; /* Add space below the search bar */
  margin-right: 20px; /* Add right margin */
}

.search-bar input {
  width: 100%; /* Full width for the input */
  padding: 10px; /* Add some padding */
  border: 1px solid #e5e7eb; /* Border style */
  border-radius: 24px; /* Rounded corners */
}
</style>
